import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/billing-issues")({
	component: BillingIssuesPage,
});

type Severity = "blocking" | "warning" | "info";
type Scope = "global" | "organization" | "subscription";
type Status = "active" | "resolved" | "all";

type BillingSyncIssue = {
	id: string;
	stableKey: string;
	scope: Scope;
	severity: Severity;
	issueType: string;
	message: string;
	organizationId: string | null;
	organizationName: string | null;
	billingCustomerId: string | null;
	billingSubscriptionId: string | null;
	chargebeeObjectId: string | null;
	chargebeeCustomerUrl: string | null;
	chargebeeSubscriptionUrl: string | null;
	billingKey: string | null;
	metadata: Record<string, unknown> | null;
	firstSeenAt: string | Date;
	lastSeenAt: string | Date;
	resolvedAt: string | Date | null;
};

const ALL = "all";

// issueType is a freely-typed backend string (new types can appear without a
// frontend change), so render it humanized rather than via a translation map.
const humanizeIssueType = (issueType: string): string =>
	issueType
		.split("_")
		.map((part) => (part ? part[0]?.toUpperCase() + part.slice(1) : part))
		.join(" ");

const severityBadgeVariant = (
	severity: Severity,
): "destructive" | "secondary" | "outline" => {
	switch (severity) {
		case "blocking":
			return "destructive";
		case "warning":
			return "secondary";
		default:
			return "outline";
	}
};

function BillingIssuesPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [severity, setSeverity] = useState<Severity | typeof ALL>(ALL);
	const [scope, setScope] = useState<Scope | typeof ALL>(ALL);
	const [status, setStatus] = useState<Status>("active");
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [selectedIssue, setSelectedIssue] = useState<BillingSyncIssue | null>(
		null,
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	const listInput = {
		severity: severity === ALL ? undefined : severity,
		scope: scope === ALL ? undefined : scope,
		status,
		search: debouncedSearch || undefined,
		limit: 100,
		offset: 0,
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "billingSyncIssues", "list", listInput],
		queryFn: async () => client.admin.billingSyncIssues.list(listInput),
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	const { data: summary } = useQuery({
		queryKey: ["admin", "billingSyncIssues", "summary"],
		queryFn: async () => client.admin.billingSyncIssues.getSummary(),
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
	});

	const issues = (data?.issues ?? []) as BillingSyncIssue[];

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="font-bold text-3xl">
						{t("admin:billingIssues.title")}
					</h1>
					<p className="text-muted-foreground">
						{t("admin:billingIssues.subtitle")}
					</p>
				</div>
				{summary && (
					<div className="flex items-center gap-2">
						<Badge variant="destructive">
							{t("admin:billingIssues.summary.blocking", {
								count: summary.blocking,
							})}
						</Badge>
						<Badge variant="secondary">
							{t("admin:billingIssues.summary.warning", {
								count: summary.warning,
							})}
						</Badge>
						<Badge variant="outline">
							{t("admin:billingIssues.summary.info", { count: summary.info })}
						</Badge>
					</div>
				)}
			</div>

			<div className="flex h-[calc(100vh-14rem)] flex-col overflow-hidden rounded-lg border bg-card shadow-sm">
				<div className="flex flex-wrap items-center gap-3 border-b p-4">
					<Input
						placeholder={t("admin:billingIssues.search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="max-w-xs"
					/>
					<Select
						value={severity}
						onValueChange={(v) => setSeverity(v as Severity | typeof ALL)}
					>
						<SelectTrigger className="w-40">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>
								{t("admin:billingIssues.filters.all_severities")}
							</SelectItem>
							<SelectItem value="blocking">
								{t("admin:billingIssues.severity.blocking")}
							</SelectItem>
							<SelectItem value="warning">
								{t("admin:billingIssues.severity.warning")}
							</SelectItem>
							<SelectItem value="info">
								{t("admin:billingIssues.severity.info")}
							</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={scope}
						onValueChange={(v) => setScope(v as Scope | typeof ALL)}
					>
						<SelectTrigger className="w-40">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>
								{t("admin:billingIssues.filters.all_scopes")}
							</SelectItem>
							<SelectItem value="global">
								{t("admin:billingIssues.scope.global")}
							</SelectItem>
							<SelectItem value="organization">
								{t("admin:billingIssues.scope.organization")}
							</SelectItem>
							<SelectItem value="subscription">
								{t("admin:billingIssues.scope.subscription")}
							</SelectItem>
						</SelectContent>
					</Select>
					<Select value={status} onValueChange={(v) => setStatus(v as Status)}>
						<SelectTrigger className="w-40">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="active">
								{t("admin:billingIssues.status.active")}
							</SelectItem>
							<SelectItem value="resolved">
								{t("admin:billingIssues.status.resolved")}
							</SelectItem>
							<SelectItem value="all">
								{t("admin:billingIssues.status.all")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<ScrollArea className="flex-1">
					<div className="p-4">
						{isLoading ? (
							<div className="space-y-3">
								{Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
									(id) => (
										<Skeleton key={id} className="h-12 w-full" />
									),
								)}
							</div>
						) : issues.length > 0 ? (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>
											{t("admin:billingIssues.columns.severity")}
										</TableHead>
										<TableHead>
											{t("admin:billingIssues.columns.issue_type")}
										</TableHead>
										<TableHead>
											{t("admin:billingIssues.columns.organization")}
										</TableHead>
										<TableHead>
											{t("admin:billingIssues.columns.message")}
										</TableHead>
										<TableHead>
											{t("admin:billingIssues.columns.chargebee")}
										</TableHead>
										<TableHead>
											{t("admin:billingIssues.columns.last_seen")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{issues.map((issue) => (
										<TableRow
											key={issue.id}
											onClick={() => setSelectedIssue(issue)}
											className="cursor-pointer"
										>
											<TableCell>
												<Badge variant={severityBadgeVariant(issue.severity)}>
													{t(`admin:billingIssues.severity.${issue.severity}`)}
												</Badge>
											</TableCell>
											<TableCell className="font-medium">
												{humanizeIssueType(issue.issueType)}
											</TableCell>
											<TableCell>
												{issue.organizationId ? (
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															navigate({
																to: `/organizations/${issue.organizationId}`,
															});
														}}
														className="flex items-center gap-1 text-left hover:underline"
													>
														{issue.organizationName ?? issue.organizationId}
														<ExternalLink className="h-3 w-3" />
													</button>
												) : (
													<span className="text-muted-foreground">
														{t("admin:billingIssues.global")}
													</span>
												)}
											</TableCell>
											<TableCell className="max-w-md truncate text-muted-foreground text-sm">
												{issue.message}
											</TableCell>
											<TableCell onClick={(e) => e.stopPropagation()}>
												<ChargebeeLinks issue={issue} />
											</TableCell>
											<TableCell className="whitespace-nowrap text-muted-foreground text-sm">
												{new Date(issue.lastSeenAt).toLocaleString()}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						) : (
							<div className="py-12 text-center">
								<AlertTriangle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
								<p className="text-muted-foreground">
									{t("admin:billingIssues.empty")}
								</p>
							</div>
						)}
					</div>
				</ScrollArea>

				{data && (
					<div className="border-t p-4">
						<p className="text-muted-foreground text-sm">
							{t("admin:billingIssues.showing", {
								count: issues.length,
								total: data.total,
							})}
						</p>
					</div>
				)}
			</div>

			<IssueDetailDrawer
				issue={selectedIssue}
				onClose={() => setSelectedIssue(null)}
			/>
		</div>
	);
}

function IssueDetailDrawer({
	issue,
	onClose,
}: {
	issue: BillingSyncIssue | null;
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<Sheet open={!!issue} onOpenChange={(open) => !open && onClose()}>
			<SheetContent className="w-full overflow-y-auto sm:max-w-lg">
				{issue && (
					<>
						<SheetHeader>
							<SheetTitle className="flex items-center gap-2">
								<Badge variant={severityBadgeVariant(issue.severity)}>
									{t(`admin:billingIssues.severity.${issue.severity}`)}
								</Badge>
								{humanizeIssueType(issue.issueType)}
							</SheetTitle>
							<SheetDescription>{issue.message}</SheetDescription>
						</SheetHeader>

						<div className="space-y-4 p-4 text-sm">
							<DetailRow
								label={t("admin:billingIssues.detail.scope")}
								value={t(`admin:billingIssues.scope.${issue.scope}`)}
							/>
							{issue.organizationId && (
								<div className="flex items-center justify-between gap-2">
									<span className="text-muted-foreground">
										{t("admin:billingIssues.detail.organization")}
									</span>
									<Button
										variant="link"
										size="sm"
										className="h-auto p-0"
										onClick={() =>
											navigate({
												to: `/organizations/${issue.organizationId}`,
											})
										}
									>
										{issue.organizationName ?? issue.organizationId}
										<ExternalLink className="ml-1 h-3 w-3" />
									</Button>
								</div>
							)}
							{issue.chargebeeCustomerUrl && (
								<div className="flex items-center justify-between gap-2">
									<span className="text-muted-foreground">
										{t("admin:billingIssues.detail.chargebee_customer")}
									</span>
									<a
										href={issue.chargebeeCustomerUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-1 font-medium text-primary hover:underline"
									>
										{t("admin:billingIssues.open_in_chargebee")}
										<ExternalLink className="h-3 w-3" />
									</a>
								</div>
							)}
							{issue.chargebeeSubscriptionUrl && (
								<div className="flex items-center justify-between gap-2">
									<span className="text-muted-foreground">
										{t("admin:billingIssues.detail.chargebee_subscription")}
									</span>
									<a
										href={issue.chargebeeSubscriptionUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-1 font-medium text-primary hover:underline"
									>
										{t("admin:billingIssues.open_in_chargebee")}
										<ExternalLink className="h-3 w-3" />
									</a>
								</div>
							)}
							<DetailRow
								label={t("admin:billingIssues.detail.stable_key")}
								value={issue.stableKey}
								mono
							/>
							{issue.chargebeeObjectId && (
								<DetailRow
									label={t("admin:billingIssues.detail.chargebee_object_id")}
									value={issue.chargebeeObjectId}
									mono
								/>
							)}
							{issue.billingKey && (
								<DetailRow
									label={t("admin:billingIssues.detail.billing_key")}
									value={issue.billingKey}
									mono
								/>
							)}
							<DetailRow
								label={t("admin:billingIssues.detail.first_seen")}
								value={new Date(issue.firstSeenAt).toLocaleString()}
							/>
							<DetailRow
								label={t("admin:billingIssues.detail.last_seen")}
								value={new Date(issue.lastSeenAt).toLocaleString()}
							/>
							<DetailRow
								label={t("admin:billingIssues.detail.resolved_at")}
								value={
									issue.resolvedAt
										? new Date(issue.resolvedAt).toLocaleString()
										: t("admin:billingIssues.detail.active")
								}
							/>

							{issue.metadata && Object.keys(issue.metadata).length > 0 && (
								<div className="space-y-1">
									<span className="text-muted-foreground">
										{t("admin:billingIssues.detail.metadata")}
									</span>
									<pre className="overflow-x-auto rounded bg-muted p-3 text-xs">
										{JSON.stringify(issue.metadata, null, 2)}
									</pre>
								</div>
							)}
						</div>
					</>
				)}
			</SheetContent>
		</Sheet>
	);
}

function ChargebeeLinks({ issue }: { issue: BillingSyncIssue }) {
	const { t } = useTranslation();

	if (!issue.chargebeeCustomerUrl && !issue.chargebeeSubscriptionUrl) {
		return <span className="text-muted-foreground text-sm">—</span>;
	}

	return (
		<div className="flex flex-col gap-1 text-sm">
			{issue.chargebeeCustomerUrl && (
				<a
					href={issue.chargebeeCustomerUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="flex items-center gap-1 text-primary hover:underline"
				>
					{t("admin:billingIssues.chargebee.customer")}
					<ExternalLink className="h-3 w-3" />
				</a>
			)}
			{issue.chargebeeSubscriptionUrl && (
				<a
					href={issue.chargebeeSubscriptionUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="flex items-center gap-1 text-primary hover:underline"
				>
					{t("admin:billingIssues.chargebee.subscription")}
					<ExternalLink className="h-3 w-3" />
				</a>
			)}
		</div>
	);
}

function DetailRow({
	label,
	value,
	mono,
}: {
	label: string;
	value: string;
	mono?: boolean;
}) {
	return (
		<div className="flex items-start justify-between gap-4">
			<span className="text-muted-foreground">{label}</span>
			<span
				className={`text-right ${mono ? "break-all font-mono text-xs" : "font-medium"}`}
			>
				{value}
			</span>
		</div>
	);
}
